import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { PageHeader, Card, Button, Badge, LoadingState } from '../components/ui';
import sessionService from '../services/sessionService';
import accountLivenessService from '../services/accountLivenessService';
import { ServerIcon, ExclamationCircleIcon, CheckCircleIcon, PlayIcon, StopIcon } from '@heroicons/react/24/outline';

const Sessions = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const queryClient = useQueryClient();
  
  const { data: sessions = [], isLoading, error } = useQuery(
    'sessions', 
    sessionService.getSessions,
    {
      refetchInterval: 6000,
      keepPreviousData: true,
    }
  );
  
  // Account liveness check mutation
  const checkAccountMutation = useMutation(
    (accountId) => accountLivenessService.checkAccount(accountId),
    {
      onSuccess: (data) => {
        console.log('Account check completed:', data);
        queryClient.invalidateQueries('sessions');
      },
      onError: (error) => {
        console.error('Failed to check account:', error);
        // Handle different error types safely
        let errorMessage = 'Unknown error occurred';
        if (error && error.response && error.response.data) {
          errorMessage = error.response.data.error || error.response.data.details || errorMessage;
        } else if (error && error.message) {
          errorMessage = error.message;
        }
        console.error('Detailed error:', errorMessage);
        // Still invalidate queries to refresh the display
        queryClient.invalidateQueries('sessions');
      }
    }
  );
  
  // Filter sessions based on search term
  const filteredSessions = sessions.filter(session => 
    session.account_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    session.phonenumber?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Get session status badge
  const getSessionStatusBadge = (session) => {
    if (session.database_exists && session.files_exists) {
      return <Badge variant="green">Active</Badge>;
    } else if (!session.database_exists && !session.files_exists) {
      return <Badge variant="gray">Not Found</Badge>;
    } else {
      return <Badge variant="yellow">Partial</Badge>;
    }
  };
  
  // Get live status badge
  const getLiveStatusBadge = (liveStatus) => {
    switch (liveStatus) {
      case 'online':
        return <Badge variant="green">Online</Badge>;
      case 'offline':
        return <Badge variant="red">Offline</Badge>;
      case 'checking':
        return <Badge variant="yellow">Checking...</Badge>;
      case 'error':
        return <Badge variant="red">Error</Badge>;
      case 'unknown':
      default:
        return <Badge variant="gray">Unknown</Badge>;
    }
  };
  
  // Handle account check
  const handleCheckAccount = (accountId) => {
    checkAccountMutation.mutate(accountId);
  };

  const formatDateTime = (value) => {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    return date.toLocaleString();
  };

  const formatRelativeTime = (value) => {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }
    const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    if (seconds < 60) {
      return `${seconds}s ago`;
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours}h ago`;
    }
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const getAuthorizationBadge = (state) => {
    switch (state) {
      case 'authorized':
        return <Badge variant="green">Authorized</Badge>;
      case 'waiting_for_code':
        return <Badge variant="yellow">Code Pending</Badge>;
      case 'waiting_for_password':
        return <Badge variant="yellow">2FA Pending</Badge>;
      case 'code_verification_pending':
      case 'password_verification_pending':
      case 'authorizing':
        return <Badge variant="blue">In Progress</Badge>;
      case 'failed':
        return <Badge variant="red">Auth Failed</Badge>;
      case 'not_authorized':
        return <Badge variant="gray">Not Authorized</Badge>;
      default:
        return <Badge variant="gray">Unknown</Badge>;
    }
  };

  const getConnectionFreshness = (checkedAt) => {
    if (!checkedAt) {
      return { label: 'Not checked yet', className: 'text-red-600' };
    }

    const parsed = new Date(checkedAt);
    if (Number.isNaN(parsed.getTime())) {
      return { label: 'Unknown check time', className: 'text-red-600' };
    }

    const ageSeconds = Math.floor((Date.now() - parsed.getTime()) / 1000);
    if (ageSeconds < 30) {
      return { label: 'Fresh now', className: 'text-green-600' };
    }

    if (ageSeconds < 180) {
      return { label: `${Math.floor(ageSeconds / 60)}m ago`, className: 'text-yellow-600' };
    }

    return { label: `${Math.floor(ageSeconds / 60)}m ago`, className: 'text-red-600' };
  };
  
  if (isLoading) {
    return <LoadingState message="Loading sessions..." />;
  }
  
  if (error) {
    return (
      <div className="text-center text-red-600">
        <p>Error loading sessions: {error.message}</p>
      </div>
    );
  }
  
  return (
    <div>
      <PageHeader 
        title="Telegram Sessions" 
        description="Manage your Telegram bot sessions"
      />
      
      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
          <div className="w-full sm:w-64 mb-4 sm:mb-0">
            <label htmlFor="search" className="sr-only">
              Search
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                id="search"
                name="search"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Search sessions"
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className="flex space-x-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Active: {sessions.filter(s => s.database_exists && s.files_exists).length}
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              Total: {sessions.length}
            </span>
          </div>
        </div>

        <div className="mb-4 text-xs text-gray-500">
          Status = session files state. Connection = real TDLib runtime connectivity.
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Account
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Connection
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Connection Issue
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Authorization
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Activity
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Database
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Files
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSessions.length > 0 ? (
                filteredSessions.map((session) => (
                  <tr key={session.account_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <ServerIcon className="flex-shrink-0 h-5 w-5 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {session.account_name || 'Unnamed'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {session.phonenumber || 'No phone'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getSessionStatusBadge(session)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {(() => {
                        const freshness = getConnectionFreshness(session.connection_last_checked_at);
                        return (
                      <div className="flex items-center space-x-2">
                        <div>
                          {getLiveStatusBadge(session.live_status)}
                          <div className={`text-xs mt-1 ${freshness.className}`}>
                            {freshness.label}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCheckAccount(session.account_id)}
                          disabled={checkAccountMutation.isLoading || session.status !== 'on'}
                          className="text-xs"
                          title={session.status === 'on' ? 'Check runtime connection' : 'Enable account to run connection check'}
                        >
                          {checkAccountMutation.isLoading && checkAccountMutation.variables === session.account_id
                            ? 'Checking...'
                            : 'Check'
                          }
                        </Button>
                      </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 max-w-xs">
                      {session.connection_error ? (
                        <div>
                          <div className="truncate" title={session.connection_error}>{session.connection_error}</div>
                          <div className="text-xs text-gray-400 mt-1">
                            Attempts: {session.connection_attempts_failed || 0}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 max-w-xs">
                      <div className="space-y-1">
                        {getAuthorizationBadge(session.authorization_state)}
                        {session.authorization_last_error && (
                          <div className="text-xs text-red-600 truncate" title={session.authorization_last_error}>
                            {session.authorization_last_error}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 max-w-xs">
                      <div className="space-y-1">
                        <div title={formatDateTime(session.last_activity_at)}>
                          {formatRelativeTime(session.last_activity_at)}
                        </div>
                        <div className="text-xs text-gray-500">
                          Last online: {formatDateTime(session.last_online_at)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {session.database_exists ? (
                        <div className="flex items-center text-green-600">
                          <CheckCircleIcon className="h-5 w-5 mr-1" />
                          <span>Yes</span>
                        </div>
                      ) : (
                        <div className="flex items-center text-red-600">
                          <ExclamationCircleIcon className="h-5 w-5 mr-1" />
                          <span>No</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {session.files_exists ? (
                        <div className="flex items-center text-green-600">
                          <CheckCircleIcon className="h-5 w-5 mr-1" />
                          <span>Yes</span>
                        </div>
                      ) : (
                        <div className="flex items-center text-red-600">
                          <ExclamationCircleIcon className="h-5 w-5 mr-1" />
                          <span>No</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {session.size_mb ? `${session.size_mb.toFixed(2)} MB` : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link 
                        to={`/sessions/${session.account_id}`} 
                        className="text-primary-600 hover:text-primary-900"
                      >
                        Details
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="px-6 py-4 text-center text-sm text-gray-500">
                    No sessions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      
      <Card title="Session Management">
        <div className="prose max-w-none">
          <p>
            Sessions are the Telegram data files associated with each account. Each account has two components:
          </p>
          <ul>
            <li><strong>Database files</strong>: Contains the account's chat history and settings</li>
            <li><strong>Files directory</strong>: Contains media files, profile pictures, etc.</li>
          </ul>
          <p>
            If an account is having connection issues, you may need to reset its session. This will delete the session files and the account will need to be re-authenticated.
          </p>
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationCircleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <strong>Warning:</strong> Resetting a session will delete all local data for that account. The account will need to be re-authenticated before it can be used again.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Sessions;
